export const RANGE_Y_PAD = 10

export function isSelectableBin(bin) {
  return Number(bin?.longQty || 0) > 0 || Number(bin?.shortQty || 0) > 0
}

export function emptyRangeStats() {
  return {
    binCount: 0,
    priceMin: 0,
    priceMax: 0,
    longQty: 0,
    shortQty: 0,
    longCostVal: 0,
    shortVal: 0,
    avgLongPrice: 0,
    avgShortPrice: 0,
    netDelta: 0,
    overlappedQty: 0,
    residualQty: 0,
    residualSide: 'flat',
    spotPnl: 0,
    shortPnl: 0,
    netPnl: 0,
    avgSpread: 0,
    hasAvgSpread: false,
  }
}

export function selectBinsByYRange(bins = [], y0, y1, pad = RANGE_Y_PAD) {
  const start = Number(y0)
  const end = Number(y1)
  if (!Number.isFinite(start) || !Number.isFinite(end)) return []
  const lo = Math.min(start, end) - Number(pad || 0)
  const hi = Math.max(start, end) + Number(pad || 0)
  return bins.filter((bin) => {
    const y = Number(bin?.y)
    return Number.isFinite(y) && y >= lo && y <= hi
  })
}

export function selectBinsByPriceRange(bins = [], priceMin, priceMax) {
  const lo = Math.min(Number(priceMin), Number(priceMax))
  const hi = Math.max(Number(priceMin), Number(priceMax))
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return []
  return bins.filter((bin) => {
    const bMin = Number(bin?.priceMin)
    const bMax = Number(bin?.priceMax)
    if (!Number.isFinite(bMin) || !Number.isFinite(bMax)) return false
    return bMin < hi && bMax > lo
  })
}

export function nearestBinByY(bins = [], y, maxDist = Infinity) {
  const target = Number(y)
  if (!Number.isFinite(target)) return null
  const limit = Number(maxDist)
  let best = null
  let bestDist = Infinity
  bins.forEach((bin) => {
    const by = Number(bin?.y)
    if (!Number.isFinite(by)) return
    const dist = Math.abs(by - target)
    if (dist <= limit && dist < bestDist) {
      best = bin
      bestDist = dist
    }
  })
  return best
}

export function envelopePriceRange(bins = []) {
  const selected = bins.filter(isSelectableBin)
  if (!selected.length) return null
  return {
    priceMin: Math.min(...selected.map((bin) => Number(bin.priceMin))),
    priceMax: Math.max(...selected.map((bin) => Number(bin.priceMax))),
  }
}

export function priceRangeFromAnchorToCurrent(bins = [], anchorBin) {
  if (!anchorBin) return null
  const aMin = Number(anchorBin.priceMin)
  const aMax = Number(anchorBin.priceMax)
  if (!Number.isFinite(aMin) || !Number.isFinite(aMax)) return null
  const current = bins.find((bin) => bin && bin.isCurrentRange)
  if (!current) {
    return { priceMin: aMin, priceMax: aMax }
  }
  return {
    priceMin: Math.min(aMin, Number(current.priceMin)),
    priceMax: Math.max(aMax, Number(current.priceMax)),
  }
}

/**
 * Soma o estoque visível de um conjunto de faixas.
 * Médias são ponderadas por notional; PnL é a soma do PnL já calculado em cada faixa.
 */
export function aggregateSelectedBins(bins = [], { includeEmpty = false } = {}) {
  const selected = includeEmpty
    ? (bins || []).filter(Boolean)
    : bins.filter(isSelectableBin)
  if (!selected.length) return emptyRangeStats()

  let longQty = 0
  let shortQty = 0
  let longCostVal = 0
  let shortVal = 0
  let spotPnl = 0
  let shortPnl = 0

  selected.forEach((bin) => {
    longQty += Number(bin.longQty) || 0
    shortQty += Number(bin.shortQty) || 0
    longCostVal += Number(bin.longCostVal) || 0
    shortVal += Number(bin.shortVal) || 0
    spotPnl += Number(bin.spotPnl) || 0
    shortPnl += Number(bin.shortPnl) || 0
  })

  const netDelta = longQty - shortQty
  const overlappedQty = Math.min(longQty, shortQty)
  const residualQty = Math.abs(netDelta)
  const avgLongPrice = longQty > 0 ? longCostVal / longQty : 0
  const avgShortPrice = shortQty > 0 ? shortVal / shortQty : 0
  const hasAvgSpread = longQty > 0 && shortQty > 0

  return {
    binCount: selected.length,
    priceMin: Math.min(...selected.map((bin) => Number(bin.priceMin))),
    priceMax: Math.max(...selected.map((bin) => Number(bin.priceMax))),
    longQty,
    shortQty,
    longCostVal,
    shortVal,
    avgLongPrice,
    avgShortPrice,
    netDelta,
    overlappedQty,
    residualQty,
    residualSide: netDelta > 0 ? 'long' : netDelta < 0 ? 'short' : 'flat',
    spotPnl,
    shortPnl,
    netPnl: spotPnl + shortPnl,
    avgSpread: hasAvgSpread ? avgShortPrice - avgLongPrice : 0,
    hasAvgSpread,
  }
}
